import { tokens } from "@fluentui/react-components";
import { ResponsiveBreakpoints, ScrollBarBorderRadius, ScrollBarHeight, ScrollBarWidth } from "../constants/StyleConstants";
/**
 * Flex mixin for elements
 * @param direction flex-direction
 * @param wrap flex-wrap
 * @returns GriffelStyle object
 */ export const flexParent = (direction = "row", wrap = "nowrap")=>({
        display: "flex",
        flexDirection: direction,
        flexWrap: wrap
    });
/**
 * For centering all children along cross axis in an element.
 * Requires calling flex-parent before use.
 */ export const flexParentCenterCrossAxis = {
    alignItems: "center"
};
/**
 * For Styling Button as a div
 * @returns GriffelStyle object
 */ export const styleButtonAsDiv = {
    display: "flex",
    backgroundColor: "transparent",
    border: "none",
    justifyContent: "inherit",
    textAlign: "inherit",
    color: "inherit",
    boxSizing: "inherit",
    padding: "0px",
    margin: "0px",
    font: "inherit"
};
/**
 * Truncation for text that commonly overflows.
 * @param overflow - "clip" or "ellipsis" to control text overflow behavior
 * @returns GriffelStyle object
 */ export const textClip = (overflow = "clip")=>({
        textOverflow: overflow,
        overflow: "hidden",
        whiteSpace: "nowrap"
    });
/**
 * Truncation for long text that overflows
 * @param lineNumber max number of lines to display before truncation
 */ export const previewOverflowEllipsis = (lineNumber = "5")=>({
        display: "-webkit-box",
        "-webkit-box-orient": "vertical",
        "-webkit-line-clamp": lineNumber,
        whiteSpace: "pre-line",
        overflow: "hidden",
        textOverflow: "ellipsis"
    });
// Flex - Used for flexible views only
// - Applies only to elements using .flex-parent(...)
export const flexChildSizeFlexible = (width = "0px")=>({
        "-webkit-flex": `1 1 ${width}`,
        "-moz-flex": `1 1 ${width}`,
        "-ms-flex": `1 1 ${width}`,
        flex: `1 1 ${width}`,
        minHeight: 0,
        /* Chrome fix - Chrome incorrectly sets a min-height it will not go lower than */ minWidth: 0
    });
// Flex - Used for fixed sized elements
// - Applies only to elements using .flex-parent(...)
export const flexChildSizeFixed = (width = "auto")=>({
        "-webkit-flex": `0 0 ${width}`,
        "-moz-flex": `0 0 ${width}`,
        "-ms-flex": `0 0 ${width}`,
        flex: `0 0 ${width}`,
        minHeight: 0,
        /* Chrome fix - Chrome incorrectly sets a min-height it will not go lower than */ minWidth: 0
    });
/**
 * For centering all children in an element.
 * Requires calling flex-parent before use.
 */ export const flexParentCenterAll = {
    justifyContent: "center",
    alignItems: "center"
};
/**
 * Styles for scrollbar using Fluent UI v9 tokens
 */ export const styleScrollbar = {
    // Standard property for non-WebKit browsers (Firefox). WCAG 1.4.11 requires
    // >=3:1 contrast for the thumb; without this, Firefox falls back to the
    // low-contrast native thumb since it ignores the ::-webkit-scrollbar-* rules.
    scrollbarColor: `${tokens.colorNeutralForeground4} transparent`,
    "::-webkit-scrollbar": {
        width: ScrollBarWidth,
        height: ScrollBarHeight
    },
    "::-webkit-scrollbar-track": {
        borderRadius: ScrollBarBorderRadius
    },
    "::-webkit-scrollbar-track-piece": {
        borderRadius: ScrollBarBorderRadius
    },
    "::-webkit-scrollbar-thumb": {
        backgroundColor: tokens.colorNeutralForeground4,
        borderRadius: ScrollBarBorderRadius
    },
    "::-webkit-scrollbar-corner": {
        backgroundColor: "transparent"
    }
};
/** The AI flair gradient for Planner */ export const PlannerFlairGradient = "linear-gradient(222deg, #A0E4FC 12.5%, #DBA9F6 86.88%)";
/** Uno Z Layers used for layering elements */ const zLayers = {
    taskEditorFullMode: 10,
    fab: 50,
    devTools: 1000,
    copilotSidecar: 1001
};
/**
 * Get uno z index layer
 * @param layer
 */ export function getZIndex(layer) {
    return zLayers[layer];
}
/**
 * Helper function to apply custom CSS at a specific breakpoint
 * @param breakpoint - The breakpoint at which to apply the styles
 * @param styles - The CSS properties to apply at the breakpoint
 * @returns GriffelStyle object with media query
 *
 * @example
 * ```typescript
 * myElement: {
 *     fontSize: tokens.fontSizeBase300,
 *     padding: tokens.spacingHorizontalL,
 *     ...responsiveBreakpoint("small", {
 *         fontSize: tokens.fontSizeBase200,
 *         padding: tokens.spacingHorizontalM,
 *     }),
 * }
 * ```
 */ export const responsiveBreakpoint = (breakpoint, styles)=>({
        [`@media ${ResponsiveBreakpoints[breakpoint]}`]: styles
    });
/**
 * "No color" swatch style: a 1px outline plus a diagonal "/" slash drawn via an
 * `::after` pseudo-element, both colored with `strokeColor`. Used to indicate an
 * empty / unassigned color. The host element must be sized; add a background if
 * the slash needs an opaque backdrop.
 * @param strokeColor - color token for the outline and diagonal line (default colorNeutralStroke2)
 * @returns GriffelStyle object
 */ export const noColorSwatchSlash = (strokeColor = tokens.colorNeutralStroke2)=>({
        position: "relative",
        overflow: "hidden",
        outline: `1px solid ${strokeColor}`,
        "::after": {
            content: '""',
            position: "absolute",
            inset: 0,
            background: `linear-gradient(
            to top right,
            transparent calc(50% - 0.75px),
            ${strokeColor} calc(50% - 0.75px),
            ${strokeColor} calc(50% + 0.75px),
            transparent calc(50% + 0.75px)
        )`
        }
    });
/**
 * Selected-state ring for a Fluent `ColorSwatch` rendered inside a `SwatchPicker`.
 *
 * Overrides Fluent v9's default selected indicator (a low-contrast `colorBrandStroke1` boxShadow)
 * which drops below the 3:1 ratio required by WCAG 1.4.11 Non-Text Contrast against many swatch
 * colors. Replaces it with a single high-contrast outset ring (`colorNeutralForeground1`, >10:1
 * against both light/dark popover backgrounds and every swatch). The `aria-checked`/`aria-selected`
 * selectors cover SwatchPicker's radio vs grid rendering modes; the forced-colors fallback uses
 * `Highlight` since browsers suppress box-shadow in High Contrast.
 *
 * Spread into a swatch's class, e.g. `colorSwatch: { borderRadius: ..., ...selectedColorSwatchRing }`.
 */ export const selectedColorSwatchRing = {
    "&[aria-checked='true'], &[aria-selected='true']": {
        boxShadow: `0 0 0 ${tokens.strokeWidthThicker} ${tokens.colorNeutralForeground1}`,
        ":hover": {
            boxShadow: `0 0 0 ${tokens.strokeWidthThicker} ${tokens.colorNeutralForeground1}`
        },
        ":hover:active": {
            boxShadow: `0 0 0 ${tokens.strokeWidthThicker} ${tokens.colorNeutralForeground1}`
        },
        "@media (forced-colors: active)": {
            boxShadow: "none",
            outline: "3px solid Highlight",
            outlineOffset: "2px"
        }
    }
};
